// @ts-strict-ignore
import React, {useState} from 'react';
import {clsx} from 'clsx';

import {useSegmentFilterController} from 'common/contexts/react/segment-filter-controller-context';
import {useTranslation} from 'common/i18n/hooks';
import {CheckboxReact} from 'common/components/checkbox';
import {ToggleTheme} from 'common/components/toggle/types';
import {ToggleReact} from 'common/components/toggle';
import {ColorPicker} from 'common/query-builder/chart-customization/colors/color-picker';
import {ColorThemeSwatch} from 'common/query-builder/chart-customization/colors/swatch';
import {getColorsForMode} from 'common/query-builder/chart-customization/colors/utils';
import {ButtonReact} from 'common/components/button';
import {useThemes} from 'common/contexts/react/themes-context';
import {DropMenuReact, PositionParent} from 'common/components/drop-menu';
import {Visibility} from 'common/components/base/stackable';
import {getSeriesMatchPath} from 'common/charts/contexts/utils';
import {useQueryContextManager} from 'common/contexts/react/query-context-manager';
import {flattenColumns, getNumSegments} from 'common/components/data-table/utils';

import styles from './index.module.styl';

type SegmentColorPickerProps = {
  segmentColor: string;
  path: string;
  numBoardReports?: number;
  positionParent: PositionParent;
  onClose: () => void;
};

export function SegmentColorPicker({
  segmentColor,
  path,
  numBoardReports,
  positionParent,
  onClose,
}: SegmentColorPickerProps) {
  const themes = useThemes();
  const {t} = useTranslation();
  const queryContextManager = useQueryContextManager();
  const segmentFilterController = useSegmentFilterController();
  const [color, setColor] = useState(segmentColor);
  const [initialColor] = useState(segmentColor);
  const [initialLegendOverrides] = useState({...segmentFilterController.globalAndLocalOverrides});
  const [applyToBoard, setApplyToBoard] = useState(false);
  const [isDefault, setIsDefault] = useState(
    !segmentFilterController.localOverrides[path] && !segmentFilterController.localOverrides[getSeriesMatchPath(path)],
  );

  // hides the board coloring checkbox when the user is actively editing a bookmark inside the report editor
  // when the report editor is loaded, the bookmark params are loaded onto the `queryContextManager` context
  const isBoardColoringEnabled =
    queryContextManager.query.bookmarkType === null && segmentFilterController.isBoardsColoringEnabled();

  const tabs = [
    {label: t(`chart-customization.theme`), value: `theme`},
    {label: t(`common.custom`), value: `custom`},
  ];

  const defaultColor = segmentFilterController.getItemColor(0, path, {ignoreOverrides: true});
  const theme = segmentFilterController.getTheme();
  const themeColors = getColorsForMode(theme, segmentFilterController.themesResource?.isDarkMode());
  const activeThemeIndex = isDefault ? null : themeColors.findIndex((c) => c === color);

  const [activeTab, setActiveTab] = useState(activeThemeIndex === -1 ? `custom` : `theme`);

  /** board overrides use a partial path match */
  const setGlobalOverride = (segmentPath: string, overrideColor: string) => {
    themes.setLegendOverride(getSeriesMatchPath(segmentPath), overrideColor ? {color: overrideColor} : null);
  };

  const handleColorChange = (updatedColor: string | null, isDefaultColor = false) => {
    setColor(updatedColor ?? defaultColor);
    setIsDefault(isDefaultColor);
    segmentFilterController.setLegendOverride(path, updatedColor ? {color: updatedColor} : null);
    if (applyToBoard) {
      setGlobalOverride(path, updatedColor);
    }
  };

  const handleApplyToBoardToggle = (on: boolean) => {
    setApplyToBoard(on);
    if (on) {
      setGlobalOverride(path, color);
    } else {
      themes.legendOverrides = {};
    }
  };

  const handleClose = () => {
    /** persist color selection on dropdown close */
    if (initialColor !== color) {
      segmentFilterController.handleLegendChanges();
    } else {
      segmentFilterController.localOverrides = {...initialLegendOverrides};
    }
    onClose();

    mixpanel.track(`[Colors] Assign Legend Color`, {
      path,
      color,
      type: activeTab,
      themeName: segmentFilterController.getTheme()?.name,
      resultsLength: segmentFilterController.sections[0]?.items?.length,
      segmentCount: getNumSegments(flattenColumns(segmentFilterController.sections[0]?.tableColumns ?? [])) - 1,
    });
  };

  React.useEffect(() => {
    segmentFilterController.notifySeriesHovered({seriesId: path});
    return () => segmentFilterController.notifySeriesHovered(null);
  }, []);

  return (
    <DropMenuReact
      open
      experimentalPositioning
      positionParent={positionParent}
      onChange={(ev) => ev.detail.state === Visibility.Closed && handleClose()}
    >
      <div className={styles.segmentColorPicker} elref='segmentColorPicker'>
        <div className={styles.tabBar}>
          <ToggleReact
            options={tabs}
            selected={activeTab}
            onSelect={(ev) => setActiveTab(ev.detail.selected)}
            theme={ToggleTheme.TabBarLite}
          />
        </div>
        {activeTab === `theme` ? (
          <div className={styles.line}>
            <ColorThemeSwatch
              theme={theme}
              hideName
              activeIndex={activeThemeIndex}
              onColorClick={(index) => handleColorChange(themeColors[index])}
            />
            <ButtonReact readonly={isDefault} pressed={isDefault} onClick={() => handleColorChange(null, true)}>
              {t(`common.default`)}
            </ButtonReact>
          </div>
        ) : (
          <ColorPicker color={color} onChange={handleColorChange} />
        )}

        {/* feature gate board level color assignment as we roll out */}
        {isBoardColoringEnabled && (
          <CheckboxReact
            checked={applyToBoard}
            onClick={() => handleApplyToBoardToggle(!applyToBoard)}
            className={clsx(styles.checkbox, {[styles.afterTheme]: activeTab === `theme`})}
          >
            {t(`chart-customization.set-color-for-all-checkbox`, {n: numBoardReports || ``})}
          </CheckboxReact>
        )}
      </div>
    </DropMenuReact>
  );
}
